/**
 * useWishlist Hook
 *
 * Custom hook for managing wishlist
 */

import { useState, useEffect, useCallback, useContext } from 'react';
import { wishlistService } from '../services/wishlistService';
import { AuthContext } from '../contexts/AuthContext';

export const useWishlist = () => {
  const { isAuthenticated } = useContext(AuthContext);

  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Derived state
  const itemCount = wishlist?.count || 0;
  const items = wishlist?.items || [];
  const isEmpty = itemCount === 0;

  /**
   * Fetch wishlist
   */
  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlist({ items: [], count: 0 });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await wishlistService.getWishlist();

      if (response.success && response.data) {
        setWishlist(response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch wishlist');
      }
    } catch (error) {
      console.error('[useWishlist] Fetch error:', error);
      setError(error.message || 'Failed to fetch wishlist');
      setWishlist({ items: [], count: 0 });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch on mount and when auth changes
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  /**
   * Add item to wishlist
   */
  const addToWishlist = useCallback(async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await wishlistService.addToWishlist(productId);

      if (response.success && response.data) {
        setWishlist(response.data);
        return { success: true, wishlist: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to add to wishlist');
      }
    } catch (error) {
      console.error('[useWishlist] Add error:', error);
      const errorMessage = error.message || 'Failed to add to wishlist';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Remove item from wishlist
   */
  const removeFromWishlist = useCallback(async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await wishlistService.removeFromWishlist(productId);

      if (response.success && response.data) {
        setWishlist(response.data);
        return { success: true, wishlist: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to remove from wishlist');
      }
    } catch (error) {
      console.error('[useWishlist] Remove error:', error);
      const errorMessage = error.message || 'Failed to remove from wishlist';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Toggle item in wishlist
   */
  const toggleWishlist = useCallback(async (productId) => {
    const isIn = isInWishlist(productId);

    if (isIn) {
      return await removeFromWishlist(productId);
    } else {
      return await addToWishlist(productId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToWishlist, removeFromWishlist]);

  /**
   * Check if product is in wishlist
   */
  const isInWishlist = useCallback((productId) => {
    if (!wishlist?.items) return false;
    return wishlist.items.some(item => item.productId === productId);
  }, [wishlist]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Refresh wishlist
   */
  const refresh = useCallback(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  return {
    // State
    wishlist,
    items,
    loading,
    error,
    isEmpty,
    itemCount,

    // Methods
    fetchWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    clearError,
    refresh
  };
};

export default useWishlist;
